import { useCallback, useState } from 'react'

import { Image, UploadImagesArgs } from '@tribeplatform/gql-client/types'

import { FileSizeLimitExceededError } from '../../types/file.size.error.js'
import { useCreateImages, useNetwork } from '../index.js'

import { readFiles } from './utils.js'

const DEFAULT_IMAGE_FILE_SIZE_LIMIT_MB = 10 * 1024 * 1024 // 10MB

export const useImageUploader = () => {
  const { mutateAsync: createImages, isLoading } = useCreateImages()
  const [loadingImages, setLoadingImages] = useState<Image[]>([])
  const { data: network } = useNetwork()
  const imageSizeLimit =
    network?.settings?.imageSizeLimit ?? DEFAULT_IMAGE_FILE_SIZE_LIMIT_MB

  const getLimitExceedingImages = useCallback(
    (files: File[]) => {
      const sizeLimitExceededImages = files.filter(
        file => file.size > imageSizeLimit,
      )

      return {
        sizeLimitExceededImages,
        limit: imageSizeLimit,
      }
    },
    [imageSizeLimit],
  )

  const uploadImages = useCallback(
    async (files: File[], options?: { height?: number; width?: number }) => {
      const { sizeLimitExceededImages, limit } = getLimitExceedingImages(files)

      if (sizeLimitExceededImages.length > 0) {
        throw new FileSizeLimitExceededError('File size limit exceeded', limit)
      }

      const result = await readFiles<Image>(files)
      setLoadingImages(result)
      try {
        const uploadArgs: UploadImagesArgs[] = files.map(
          file =>
            ({
              file,
              size: file.size,
              contentType: file.type,
              name: file.name,
              width: options?.width,
              height: options?.height,
            }) satisfies UploadImagesArgs,
        )
        return await createImages(uploadArgs)
      } catch (error) {
        setLoadingImages([])
        throw error
      } finally {
        setLoadingImages([])
      }
    },
    [createImages, getLimitExceedingImages],
  )

  const uploadImage = useCallback(
    async (file: File, options?: { height?: number; width?: number }) => {
      const files = [file]
      const [uploadedImage] = await uploadImages(files, options)
      return uploadedImage
    },
    [uploadImages],
  )

  return {
    uploadImage,
    uploadImages,
    loadingImages,
    getLimitExceedingImages,
    isLoading,
  }
}
